import { useQuery } from "@apollo/client"
import React, { useState } from "react"
import { ApiMonitoringPeriod } from "~/__generated__/graphql"
import { useHeaderText } from "~/common/HeaderContext"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { GraphqlError } from "~/components/GraphqlError"
import { Card, CardContent, CardHeader, CardTitle } from "~/shadcn/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/shadcn/ui/select"
import { LoadingIndicatorCentered } from "~/ui/LoadingIndicator"
import { API_MONITORING_STATS_QUERY } from "./api"
import { PlatformBreakdown } from "./components/PlatformBreakdown"
import { SuccessRateChart } from "./components/SuccessRateChart"
import { TopInfluencersTable } from "./components/TopInfluencersTable"
import { VolumeChart } from "./components/VolumeChart"

const PERIOD_OPTIONS: { value: ApiMonitoringPeriod; label: string }[] = [
  { value: ApiMonitoringPeriod.Last_24H, label: "Last 24 Hours" },
  { value: ApiMonitoringPeriod.Last_7D, label: "Last 7 Days" },
  { value: ApiMonitoringPeriod.Last_30D, label: "Last 30 Days" },
  { value: ApiMonitoringPeriod.CurrentMonth, label: "Current Month" },
]

function formatRate(rate: number): string {
  return `${rate.toFixed(1)}%`
}

const VIEW_API_MONITORING_PROPS = {}

export const ApiMonitoringScreen: React.FC = () => {
  useTrackScreenEvent("view_api_monitoring", VIEW_API_MONITORING_PROPS)
  const [period, setPeriod] = useState<ApiMonitoringPeriod>(
    ApiMonitoringPeriod.Last_7D
  )

  useHeaderText({ header: "API Monitoring", subheader: null })

  const { data, loading, error } = useQuery(API_MONITORING_STATS_QUERY, {
    variables: { period },
  })

  return (
    <div className="overflow-auto p-4 space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Period</label>
        <Select
          value={period}
          onValueChange={(v) => setPeriod(v as ApiMonitoringPeriod)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <LoadingIndicatorCentered />}

      {error && <GraphqlError error={error} />}

      {!loading && !error && data && (
        <>
          <KpiCards stats={data.apiMonitoringStats} />

          {data.apiMonitoringStats.totalRequests === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-gray-500">
                No API requests recorded for the selected period.
              </CardContent>
            </Card>
          ) : (
            <>
              <VolumeChart
                data={data.apiMonitoringStats.requestsByStatusOverTime}
              />

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SuccessRateChart
                  data={data.apiMonitoringStats.requestsByStatusOverTime}
                />
                <PlatformBreakdown
                  data={data.apiMonitoringStats.requestsByPlatform}
                />
              </div>

              <TopInfluencersTable
                data={data.apiMonitoringStats.requestsByInfluencer}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

const KpiCards: React.FC<{
  stats: {
    totalRequests: number
    successRate: number
    rateLimitRate: number
    timeoutCount: number
  }
}> = ({ stats }) => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
    <KpiCard
      label="Total Requests"
      value={stats.totalRequests.toLocaleString()}
    />
    <KpiCard
      label="Success Rate"
      value={formatRate(stats.successRate)}
      highlight={stats.successRate < 50 ? "red" : undefined}
    />
    <KpiCard
      label="Rate-Limit Rate"
      value={formatRate(stats.rateLimitRate)}
      highlight={stats.rateLimitRate > 20 ? "amber" : undefined}
    />
    <KpiCard
      label="Timeout Count"
      value={stats.timeoutCount.toLocaleString()}
      highlight={stats.timeoutCount > 10 ? "red" : undefined}
    />
  </div>
)

const KpiCard: React.FC<{
  label: string
  value: string
  highlight?: "red" | "amber"
}> = ({ label, value, highlight }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-gray-500">
        {label}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div
        className={`text-2xl font-semibold ${
          highlight === "red"
            ? "text-red-600"
            : highlight === "amber"
            ? "text-amber-500"
            : ""
        }`}
      >
        {value}
      </div>
    </CardContent>
  </Card>
)
